import { useEffect, useState } from "react";
import { useDraft } from "../hooks/useDraft";
import { useImageGeneration } from "../hooks/useImageGeneration";
import { useSuggestImagePrompt } from "../hooks/useSuggestImagePrompt";
import { ImageCandidateGrid } from "./ImageCandidateGrid";

interface Props {
  postId: string;
}

export const ImageStudio = ({ postId }: Props) => {
  const { data: draft } = useDraft(postId);
  const [prompt, setPrompt] = useState("");
  const suggest = useSuggestImagePrompt();
  const gen = useImageGeneration();

  const candidates = draft?.imageCandidates ?? [];
  const isGenerating = gen.phase === "generating" || gen.phase === "fetching_voice";

  // Sync textárea com último prompt usado quando aparece nova candidata.
  // useEffect (não inline na render) pra evitar setState-during-render.
  useEffect(() => {
    if (prompt === "" && candidates.length > 0) {
      setPrompt(candidates[candidates.length - 1].prompt);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidates.length]);

  if (!draft) return null;

  const handleFirstGenerate = async () => {
    try {
      const { prompt: suggested } = await suggest.mutateAsync(postId);
      setPrompt(suggested);
      gen.generate(postId, suggested, crypto.randomUUID());
    } catch {
      // erro já está em suggest.error pelo react-query — UI vai exibir
    }
  };

  const handleRegenerate = () => {
    if (!prompt.trim()) return;
    gen.generate(postId, prompt, crypto.randomUUID());
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4">
      <h3 className="mb-2 text-sm font-semibold text-slate-700">Imagem</h3>

      {candidates.length === 0 && (
        <div className="text-center">
          <p className="mb-3 text-sm text-slate-500">
            Gere uma imagem editorial pra acompanhar este post.
          </p>
          <button
            type="button"
            onClick={handleFirstGenerate}
            disabled={isGenerating || suggest.isPending}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {isGenerating ? "Gerando..." : suggest.isPending ? "Sugerindo prompt..." : "Gerar imagem"}
          </button>
          {suggest.isError && (
            <p className="mt-2 text-xs text-red-600">
              Falha ao sugerir prompt:{" "}
              {suggest.error instanceof Error ? suggest.error.message : "erro desconhecido"}
            </p>
          )}
          {gen.error && <p className="mt-2 text-xs text-red-600">{gen.error}</p>}
        </div>
      )}

      {candidates.length > 0 && (
        <div className="space-y-3">
          <ImageCandidateGrid
            draftId={postId}
            candidates={candidates}
            selectedImageId={draft.selectedImageId}
          />
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Prompt visual</label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-slate-300 p-2 text-sm"
              placeholder="Ex: editorial photo of a glass marble, soft window light, navy palette..."
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleRegenerate}
              disabled={isGenerating || !prompt.trim()}
              className="rounded-lg bg-slate-700 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
            >
              {isGenerating ? "Gerando..." : "Regenerar"}
            </button>
            <p className="text-xs text-slate-500">
              {candidates.length} candidata{candidates.length > 1 ? "s" : ""} &bull;{" "}
              {draft.selectedImageId ? "1 selecionada" : "nenhuma selecionada"}
            </p>
          </div>
          {gen.error && <p className="text-xs text-red-600">{gen.error}</p>}
        </div>
      )}
    </section>
  );
};
